import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';

// --- Animated Counter Hook ---
function useCountUp(target: string, duration: number, isVisible: boolean) {
  const [display, setDisplay] = useState('0');

  useEffect(() => {
    if (!isVisible) return;

    // Parse prefix, number, suffix from strings like '1,883%', '$0', '40%', '11.5%'
    const match = target.match(/^([^0-9]*)([0-9,\.]+)([^0-9]*)$/);
    if (!match) {
      setDisplay(target);
      return;
    }

    const prefix = match[1];
    const rawNumber = parseFloat(match[2].replace(/,/g, ''));
    const suffix = match[3];
    const hasComma = match[2].includes(',');
    const decimals = (match[2].split('.')[1] || '').length;

    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = rawNumber * eased;

      let formatted: string;
      if (decimals > 0) {
        formatted = current.toFixed(decimals);
      } else {
        const rounded = Math.round(current);
        formatted = hasComma ? rounded.toLocaleString('en-US') : String(rounded);
      }

      setDisplay(`${prefix}${formatted}${suffix}`);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [isVisible, target, duration]);

  return display;
}

// --- Stat Card ---
function StatCard({ stat, label, isVisible }: { stat: string; label: string; isVisible: boolean }) {
  const animated = useCountUp(stat, 1800, isVisible);
  return (
    <div className="bg-white rounded-xl p-6 text-center transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-default">
      <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4">
        <i className="ri-arrow-up-line text-[32px] text-[#A7D0CC]"></i>
      </div>
      <div className="font-['Oxygen'] font-bold text-[36px] md:text-[42px] text-black mb-2">
        {animated}
      </div>
      <p className="text-gray-600 text-[14px] leading-[1.5]">{label}</p>
    </div>
  );
}

export default function RelocationPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    relocatingFrom: '',
    situation: '',
    timeline: '',
    budget: '',
    message: ''
  });
  const [guideEmail, setGuideEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [guideSubmitting, setGuideSubmitting] = useState(false);
  const [guideSuccess, setGuideSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    if (!statsRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.title = 'Miami Relocation Specialist | Elisabet Sierra Bermejo';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Relocating to Miami? Elisabet Sierra Bermejo helps executives and professionals find the right neighborhood, home, and strategy. Free relocation consultation.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'miami relocation specialist, moving to miami, miami real estate executive relocation, brickell apartments, coconut grove homes, miami neighborhood guide, relocating to miami 2026');
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${siteUrl}/relocation`);

    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    const ogTags = [
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: `${siteUrl}/relocation` },
      { property: 'og:title', content: 'Miami Relocation Specialist | Elisabet Sierra Bermejo' },
      { property: 'og:description', content: 'Relocating to Miami? Elisabet Sierra Bermejo helps executives and professionals find the right neighborhood, home, and strategy. Free relocation consultation.' },
      { property: 'og:image', content: 'https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/142ce1355e7fe0cb96ea69f1f0b28b9d.jpeg' }
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    const twitterTags = [
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: `${siteUrl}/relocation` },
      { property: 'twitter:title', content: 'Miami Relocation Specialist | Elisabet Sierra Bermejo' },
      { property: 'twitter:description', content: 'Relocating to Miami? Elisabet Sierra Bermejo helps executives and professionals find the right neighborhood, home, and strategy. Free relocation consultation.' },
      { property: 'twitter:image', content: 'https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/142ce1355e7fe0cb96ea69f1f0b28b9d.jpeg' }
    ];

    twitterTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Miami Relocation Specialist | Elisabet Sierra Bermejo",
      "description": "Relocating to Miami? Elisabet Sierra Bermejo helps executives and professionals find the right neighborhood, home, and strategy. Free relocation consultation.",
      "url": `${siteUrl}/relocation`
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Should I rent or buy when relocating to Miami?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most executives rent for 6-12 months first. It gives you time to understand neighborhoods, commute patterns, and lifestyle fit before committing to a purchase. We help with both."
          }
        },
        {
          "@type": "Question",
          "name": "How fast does the Miami market move?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Very fast. Quality properties in desirable areas (Brickell, Edgewater, Coconut Grove) often receive multiple offers within 48-72 hours. Having pre-approval and a responsive agent is critical."
          }
        },
        {
          "@type": "Question",
          "name": "Can foreign nationals buy property in Miami?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Foreign buyers can purchase U.S. real estate. We coordinate with international lenders, help structure entities (LLC/Corp), and navigate tax implications. No U.S. credit history required for many financing options."
          }
        },
        {
          "@type": "Question",
          "name": "What are the best neighborhoods for tech professionals near Brickell?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Brickell (walkable to offices), Edgewater (waterfront, newer buildings), Coconut Grove (family-friendly, green spaces), and Wynwood (creative, younger crowd). We'll match you based on commute, lifestyle, and budget."
          }
        },
        {
          "@type": "Question",
          "name": "Do you charge consultation fees?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. The initial relocation consultation is free. If you decide to work with us, we're compensated by the seller or landlord—standard in U.S. real estate. You pay nothing for our buyer/tenant representation."
          }
        }
      ]
    };

    let webPageSchemaScript = document.querySelector('script[data-schema="webpage"]');
    if (!webPageSchemaScript) {
      webPageSchemaScript = document.createElement('script');
      webPageSchemaScript.setAttribute('type', 'application/ld+json');
      webPageSchemaScript.setAttribute('data-schema', 'webpage');
      document.head.appendChild(webPageSchemaScript);
    }
    webPageSchemaScript.textContent = JSON.stringify(webPageSchema);

    let faqSchemaScript = document.querySelector('script[data-schema="faq"]');
    if (!faqSchemaScript) {
      faqSchemaScript = document.createElement('script');
      faqSchemaScript.setAttribute('type', 'application/ld+json');
      faqSchemaScript.setAttribute('data-schema', 'faq');
      document.head.appendChild(faqSchemaScript);
    }
    faqSchemaScript.textContent = JSON.stringify(faqSchema);
  }, []);

  const scrollToForm = (situation?: string) => {
    const formSection = document.getElementById('relocation-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
      if (situation) {
        setTimeout(() => {
          setFormData(prev => ({ ...prev, situation }));
        }, 500);
      }
    }
  };

  const scrollToGuide = () => {
    const guideSection = document.getElementById('lead-magnet');
    if (guideSection) {
      guideSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const formBody = new URLSearchParams();
    formBody.append('name', formData.name);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('relocatingFrom', formData.relocatingFrom);
    formBody.append('situation', formData.situation);
    formBody.append('timeline', formData.timeline);
    formBody.append('budget', formData.budget);
    formBody.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6c8o8rv53dtlm7j81bg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', relocatingFrom: '', situation: '', timeline: '', budget: '', message: '' });
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuideSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuideSubmitting(true);

    const formBody = new URLSearchParams();
    formBody.append('email', guideEmail);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6c8o8rv53dtlm7j81b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        window.open('https://drive.google.com/uc?export=download&id=1d7X9YMFOwrX4qfjEZkSe8vGvcPPZwjNR', '_blank');
        setGuideEmail('');
        navigate('/thank-you-guide');
      }
    } catch {
      // Silent fail
    } finally {
      setGuideSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Quicksand']">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-black pt-32 md:pt-40 pb-16 md:pb-24 lg:pb-28 flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-[#1a1a1a]"></div>
        <div className="relative max-w-[1400px] w-full mx-auto px-6 md:px-10 lg:px-20">
          <div className="max-w-[900px]">
            <h1 className="font-['Oxygen'] font-bold text-[42px] md:text-[64px] lg:text-[72px] leading-[1.1] text-white mb-5 md:mb-6">
              You're Moving to Miami.<br />Let's Make It Right.
            </h1>
            <p className="text-white/85 text-[17px] md:text-[20px] lg:text-[22px] leading-[1.65] mb-6 md:mb-8 max-w-[780px]">
              The Miami market moves fast. Whether your company just announced a relocation or you're making the move yourself — you need someone who knows the neighborhoods, the timing, and the process. Before the wave arrives.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 md:gap-6 mb-5 md:mb-6">
              <style>{`
                @keyframes subtlePulse {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.05); }
                }
                .btn-pulse {
                  animation: subtlePulse 2s ease-in-out infinite;
                }
                .btn-pulse:hover {
                  animation: none;
                }
              `}</style>
              <a
                href="https://calendly.com/hello-miamicomfort/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pulse w-full sm:w-auto px-8 py-4 bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 cursor-pointer shadow-md min-h-[52px] whitespace-nowrap flex items-center justify-center"
              >
                <span className="text-black text-[16px] font-bold font-['Oxygen']">Book a Free Relocation Call</span>
              </a>
              <button
                onClick={scrollToGuide}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                className="w-full sm:w-auto px-8 py-4 bg-[#ec9982] rounded-lg text-white text-[16px] font-bold font-['Oxygen'] hover:bg-[#e08a72] transition-all duration-200 cursor-pointer whitespace-nowrap shadow-md min-h-[52px] flex items-center justify-center"
                style={{ transition: 'transform 0.2s ease, background-color 0.3s' }}
              >
                Download the Miami Neighborhood Guide →
              </button>
            </div>
            <p className="text-[#A7D0CC] text-[14px] md:text-[15px] leading-[1.6]">
              <strong>Palantir</strong> · <strong>Amazon</strong> · <strong>Citadel</strong> · <strong>ServiceNow</strong> — executives from these companies have already made Miami their home.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white py-12 md:py-16 border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {[
              { icon: 'ri-shield-check-line', text: 'Licensed FL Real Estate Agent' },
              { icon: 'ri-global-line', text: 'International Relocation Experience' },
              { icon: 'ri-map-pin-line', text: 'Miami-Dade & Broward' },
              { icon: 'ri-cpu-line', text: 'AI-Powered Market Intelligence' }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-[40px] text-[#A7D0CC]`}></i>
                </div>
                <p className="text-black text-[15px] font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="bg-[#D9CEBA] py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] lg:text-[56px] text-black mb-12 md:mb-16 text-center">
            Miami Is Moving Fast.<br />Your Window Is Narrow.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-20">
            <div>
              <p className="text-black text-[18px] md:text-[20px] lg:text-[22px] leading-[1.75] mb-6">
                Miami is experiencing an unprecedented corporate migration wave. Companies like <strong>Palantir</strong>, <strong>Amazon</strong>, <strong>JPMorgan</strong>, and <strong>ServiceNow</strong> are establishing major operations here—bringing thousands of high-earning professionals with them.
              </p>
              <p className="text-black text-[18px] md:text-[20px] lg:text-[22px] leading-[1.75]">
                The result? Inventory is tight, competition is fierce, and the best properties move within days. If you're relocating, you need someone who understands this market intimately and can move at its speed.
              </p>
            </div>
            <div ref={statsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { stat: '1,883%', label: 'Florida billionaire growth since 2001' },
                { stat: '$0', label: 'Florida state income tax' },
                { stat: '40%', label: 'Miami transactions closed in all cash' },
                { stat: '11.5%', label: 'Class A office rent increase YoY' }
              ].map((item, index) => (
                <StatCard key={index} stat={item.stat} label={item.label} isVisible={statsVisible} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Three Profiles */}
      <section className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] text-black mb-12 md:mb-16 text-center">
            Who We Work With
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                title: 'Your company is moving. You have 60 days.',
                description: 'Tech executives and professionals navigating corporate relocations. You need housing fast, in the right neighborhood, without the usual 6-month learning curve.',
                situation: 'corporate-relocation'
              },
              {
                title: "You're done paying state income tax.",
                description: 'NY and CA professionals making the move for tax optimization and lifestyle. You want strategic guidance on where to live, what to buy, and how to structure your move.',
                situation: 'tax-optimization'
              },
              {
                title: 'First time navigating the U.S. market.',
                description: 'International professionals and investors entering the U.S. real estate market. You need someone who understands cross-border complexities and can guide you through every step.',
                situation: 'international-buyer'
              }
            ].map((profile, index) => (
              <div
                key={index}
                className="bg-[#E8E2DB] rounded-2xl p-8 md:p-10 hover:shadow-xl hover:translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <h3 className="font-['Oxygen'] font-bold text-[24px] md:text-[26px] text-black mb-4 leading-[1.3]">
                  {profile.title}
                </h3>
                <p className="text-gray-700 text-[15px] md:text-[16px] leading-[1.7] mb-6">
                  {profile.description}
                </p>
                <button
                  onClick={() => scrollToForm(profile.situation)}
                  className="text-[#A7D0CC] text-[15px] font-bold hover:text-black transition-colors cursor-pointer whitespace-nowrap"
                >
                  This is me →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eli Bio */}
      <section className="bg-black py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-20 items-center">
            <div className="flex flex-col items-center lg:items-center text-center lg:text-center">
              <div className="w-48 h-48 md:w-56 md:h-56 rounded-full overflow-hidden mb-6 border-4 border-[#A7D0CC]">
                <img
                  src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/9029bce373ac6db607735eaa6d451a75.jpeg"
                  alt="Elisabet Sierra Bermejo"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h3 className="font-['Oxygen'] font-bold text-[28px] md:text-[32px] text-white mb-2">
                Elisabet Sierra Bermejo
              </h3>
              <p className="text-white/70 text-[15px] mb-6">Founder · Licensed Real Estate Sales Associate | Avanti Way Realty, LLC</p>
              <div className="grid grid-cols-2 gap-4 w-full max-w-[400px]">
                {[
                  { icon: 'ri-map-pin-line', text: 'Miami since 2018' },
                  { icon: 'ri-global-line', text: 'Madrid · Shanghai · Miami' },
                  { icon: 'ri-line-chart-line', text: 'AI-powered market analysis' },
                  { icon: 'ri-phone-line', text: '+1 786 856 8145' }
                ].map((badge, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                    <i className={`${badge.icon} text-[16px] text-black`}></i>
                    <span className="text-black text-[12px] font-medium">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="border-l-4 border-[#A7D0CC] pl-6 md:pl-8">
                <p className="text-white/90 text-[17px] md:text-[19px] leading-[1.75] mb-6">
                  "I've lived the relocation experience myself—three times, across three continents. I know what it's like to land in a new city with a tight timeline, unfamiliar neighborhoods, and high stakes."
                </p>
                <p className="text-white/90 text-[17px] md:text-[19px] leading-[1.75] mb-6">
                  "That's why I built Miami Comfort Group differently. We don't just show properties—we provide strategic guidance from day one. Where to live based on your commute and lifestyle. How to structure your purchase or rental. What the market really looks like beyond the listings."
                </p>
                <p className="text-white/90 text-[17px] md:text-[19px] leading-[1.75]">
                  "Whether you're coming from New York, São Paulo, or Singapore—I've been there. And I'll make sure your Miami transition is seamless, strategic, and stress-free."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[#D9CEBA] py-16 md:py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] text-black mb-12 md:mb-16 text-center">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              { number: '1', title: 'Tell us about your move', description: 'Share your timeline, budget, and what matters most in your new Miami home.' },
              { number: '2', title: 'Get your personal market brief', description: 'Receive a curated analysis of neighborhoods, inventory, and pricing tailored to you.' },
              { number: '3', title: 'See the right options', description: 'We show you properties that match your criteria—no time wasted on wrong fits.' },
              { number: '4', title: 'Move in with confidence', description: 'From offer to closing (or lease signing), we handle every detail so you can focus on your transition.' }
            ].map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 md:p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-black flex items-center justify-center">
                  <span className="font-['Oxygen'] font-bold text-[28px] text-white">{step.number}</span>
                </div>
                <h3 className="font-['Oxygen'] font-bold text-[20px] text-black mb-3">{step.title}</h3>
                <p className="text-gray-600 text-[15px] leading-[1.6]">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="relocation-form" className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-[680px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] text-black mb-4">
              Let's Plan Your Move
            </h2>
            <p className="text-gray-600 text-[17px] md:text-[19px]">
              Free 20-minute relocation consultation. No commitment.
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-[#A7D0CC]/20 border border-[#A7D0CC]/40 rounded-2xl p-8 md:p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#A7D0CC] flex items-center justify-center">
                <i className="ri-check-line text-[32px] text-black"></i>
              </div>
              <h3 className="font-['Oxygen'] font-bold text-[28px] text-black mb-4">
                We'll Be In Touch Soon!
              </h3>
              <p className="text-gray-700 text-[17px]">
                Elisabet will personally respond within 24 hours.
              </p>
            </div>
          ) : (
            <form
              id="relocation-consultation-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black placeholder-gray-400 focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px]"
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black placeholder-gray-400 focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px]"
                required
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black placeholder-gray-400 focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px]"
                required
              />
              <select
                name="relocatingFrom"
                value={formData.relocatingFrom}
                onChange={(e) => setFormData({ ...formData, relocatingFrom: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px] cursor-pointer"
                required
              >
                <option value="">Relocating from...</option>
                <option value="new-york">New York</option>
                <option value="california">California</option>
                <option value="texas">Texas</option>
                <option value="international-europe">International - Europe</option>
                <option value="international-latam">International - Latin America</option>
                <option value="international-asia">International - Asia</option>
                <option value="other-us">Other U.S. State</option>
                <option value="already-miami">Already in Miami</option>
              </select>
              <select
                name="situation"
                value={formData.situation}
                onChange={(e) => setFormData({ ...formData, situation: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px] cursor-pointer"
                required
              >
                <option value="">My situation...</option>
                <option value="corporate-relocation">Corporate relocation (company moving me)</option>
                <option value="tax-optimization">Tax optimization (leaving high-tax state)</option>
                <option value="international-buyer">International buyer/investor</option>
                <option value="lifestyle-move">Lifestyle move (personal choice)</option>
              </select>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px] cursor-pointer"
                required
              >
                <option value="">Timeline...</option>
                <option value="asap">ASAP (within 30 days)</option>
                <option value="1-3-months">1-3 months</option>
                <option value="3-6-months">3-6 months</option>
                <option value="exploring">Just exploring</option>
              </select>
              <select
                name="budget"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className="w-full h-14 bg-white border border-gray-300 rounded-lg px-5 text-black focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px] cursor-pointer"
                required
              >
                <option value="">Budget range...</option>
                <option value="rent-2k-4k">Rent: $2,000-$4,000/month</option>
                <option value="rent-4k-7k">Rent: $4,000-$7,000/month</option>
                <option value="rent-7k-plus">Rent: $7,000+/month</option>
                <option value="buy-500k-1m">Buy: $500K-$1M</option>
                <option value="buy-1m-2m">Buy: $1M-$2M</option>
                <option value="buy-2m-plus">Buy: $2M+</option>
                <option value="flexible">Flexible</option>
              </select>
              <textarea
                name="message"
                placeholder="Tell us more about what you're looking for... (optional)"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 500) })}
                className="w-full h-32 bg-white border border-gray-300 rounded-lg px-5 py-4 text-black placeholder-gray-400 focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px] resize-none"
                maxLength={500}
              ></textarea>

              {submitError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-600 text-[14px]">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-pulse w-full h-[56px] bg-black rounded-lg hover:bg-gray-800 transition-all duration-300 flex items-center justify-center cursor-pointer shadow-md min-h-[48px] whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="font-['Oxygen'] font-bold text-[17px] text-white">
                  {isSubmitting ? 'Sending...' : 'Book My Free Relocation Call'}
                </span>
              </button>

              <p className="text-center text-gray-500 text-[14px] leading-[1.6]">
                Elisabet will personally respond within 24 hours. No spam. No sales pressure.
              </p>

              <div className="flex items-center justify-center gap-2 pt-2">
                <span className="text-[#888888] text-[13px]">⊜</span>
                <span className="text-[#888888] text-[13px]">Equal Housing Opportunity</span>
              </div>

              <div className="text-center pt-4">
                <a
                  href="https://wa.me/17868568145"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#25D366] text-[15px] font-medium hover:text-[#1DA851] transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-[20px]"></i>
                  <span>Or message us on WhatsApp</span>
                </a>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Lead Magnet */}
      <section id="lead-magnet" className="bg-black py-16 md:py-24 lg:py-32">
        <div className="max-w-[680px] mx-auto px-6 md:px-10 lg:px-20 text-center">
          <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[48px] text-white mb-6">
            Not sure where to start?
          </h2>
          <p className="text-white/80 text-[17px] md:text-[19px] leading-[1.7] mb-10 max-w-[580px] mx-auto">
            Download our <strong>Miami Neighborhood Guide for Relocating Professionals</strong>—a detailed breakdown of Brickell, Edgewater, Coconut Grove, Wynwood, and more. Includes commute times, lifestyle fit, and average pricing.
          </p>

          {guideSuccess ? (
            <div className="bg-[#A7D0CC]/20 border border-[#A7D0CC]/40 rounded-xl p-8 text-center">
              <i className="ri-check-circle-line text-[48px] text-[#A7D0CC] mb-4"></i>
              <p className="text-white text-[17px]">Check your email for the guide!</p>
            </div>
          ) : (
            <form
              id="neighborhood-guide-form"
              data-readdy-form
              onSubmit={handleGuideSubmit}
              className="flex flex-col sm:flex-row gap-4 max-w-[500px] mx-auto"
            >
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={guideEmail}
                onChange={(e) => setGuideEmail(e.target.value)}
                className="flex-1 h-14 bg-white/10 border border-white/30 rounded-lg px-5 text-white placeholder-white/50 focus:border-[#A7D0CC] focus:outline-none transition-colors text-[15px]"
                required
              />
              <button
                type="submit"
                disabled={guideSubmitting}
                className="h-14 px-8 bg-[#A7D0CC] rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 cursor-pointer shadow-md whitespace-nowrap flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="font-['Oxygen'] font-bold text-[16px] text-black">
                  {guideSubmitting ? 'Sending...' : 'Download Guide'}
                </span>
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 md:py-24 lg:py-32">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-20">
          <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] text-black mb-12 md:mb-16 text-center">
            Common Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                question: 'Should I rent or buy when relocating to Miami?',
                answer: 'Most executives rent for 6-12 months first. It gives you time to understand neighborhoods, commute patterns, and lifestyle fit before committing to a purchase. We help with both.'
              },
              {
                question: 'How fast does the Miami market move?',
                answer: 'Very fast. Quality properties in desirable areas (Brickell, Edgewater, Coconut Grove) often receive multiple offers within 48-72 hours. Having pre-approval and a responsive agent is critical.'
              },
              {
                question: 'Can foreign nationals buy property in Miami?',
                answer: 'Yes. Foreign buyers can purchase U.S. real estate. We coordinate with international lenders, help structure entities (LLC/Corp), and navigate tax implications. No U.S. credit history required for many financing options.'
              },
              {
                question: 'What are the best neighborhoods for tech professionals near Brickell?',
                answer: 'Brickell (walkable to offices), Edgewater (waterfront, newer buildings), Coconut Grove (family-friendly, green spaces), and Wynwood (creative, younger crowd). We\'ll match you based on commute, lifestyle, and budget.'
              },
              {
                question: 'Do you charge consultation fees?',
                answer: 'No. The initial relocation consultation is free. If you decide to work with us, we\'re compensated by the seller or landlord—standard in U.S. real estate. You pay nothing for our buyer/tenant representation.'
              }
            ].map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 md:px-8 py-5 md:py-6 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
                >
                  <h4 className="font-['Oxygen'] font-bold text-[17px] md:text-[19px] text-black pr-4">
                    {faq.question}
                  </h4>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-[24px] text-gray-400 flex-shrink-0`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 md:px-8 py-5 md:py-6 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 text-[15px] md:text-[16px] leading-[1.7]">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-black py-20 md:py-28 lg:py-36">
        <div className="max-w-[800px] mx-auto px-6 md:px-10 lg:px-20 text-center">
          <h2 className="font-['Oxygen'] font-bold text-[44px] md:text-[56px] lg:text-[64px] text-white mb-6 leading-[1.1]">
            Miami is ready.<br />Are you?
          </h2>
          <p className="text-white/80 text-[18px] md:text-[20px] mb-10 md:mb-12">
            Let's find your place before someone else does.
          </p>
          <button
            onClick={() => scrollToForm()}
            className="inline-flex items-center justify-center px-10 py-5 bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 cursor-pointer shadow-lg min-h-[56px] whitespace-nowrap"
          >
            <span className="text-black text-[17px] font-bold font-['Oxygen']">Book a Free Relocation Call →</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#E8E2DB] py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <Link to="/" className="text-black text-[15px] font-medium hover:text-[#A7D0CC] transition-colors">Home</Link>
              <Link to="/about" className="text-black text-[15px] font-medium hover:text-[#A7D0CC] transition-colors">About</Link>
              <Link to="/relocation" className="text-[#A7D0CC] text-[15px] font-medium">Relocation</Link>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://www.instagram.com/elisabet_comfyhouses/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                <i className="ri-instagram-line text-[22px] text-black hover:text-[#A7D0CC] transition-colors"></i>
              </a>
              <a href="https://www.linkedin.com/in/elisabet-sierra/" target="_blank" rel="noopener noreferrer" className="w-6 h-6 flex items-center justify-center cursor-pointer">
                <i className="ri-linkedin-line text-[22px] text-black hover:text-[#A7D0CC] transition-colors"></i>
              </a>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-black/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-black/60 text-[14px]">© 2026 Miami Comfort Group. All rights reserved.</p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black/60 text-[14px] hover:text-[#A7D0CC] transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
          <div className="mt-8 pt-6 border-t border-black/10 text-center space-y-3">
            <p className="text-black font-bold text-[11px] leading-[1.6]">
              Elisabet Sierra Bermejo · Licensed Real Estate Sales Associate<br />
              Avanti Way Realty, LLC · License #SL3535387
            </p>
            <p className="text-[#888888] text-[10px] leading-[1.5] max-w-[800px] mx-auto">
              Miami Comfort Group LLC is a brand name used for marketing purposes only. Real estate services are provided by Elisabet Sierra Bermejo, licensed with Avanti Way Realty, LLC.
            </p>
            <p className="text-[#888888] text-[10px] text-[1.5]">
              © 2026 Miami Comfort Group · miamicomfort.group · hello@miamicomfort.group
            </p>
            <p className="text-[#888888] text-[9px] leading-[1.5] max-w-[700px] mx-auto">
              Equal Housing Opportunity. We are pledged to the letter and spirit of U.S. policy for equal housing opportunity throughout the nation.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
